import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mandala } from "./Mandala";
import { Toran } from "./Toran";
import { MonogramFrame } from "./MonogramFrame";
import { Countdown } from "./Countdown";
import { weddingConfig } from "../config/weddingConfig";

const EASE = [0.22, 1, 0.36, 1];

// Masked line-by-line reveal — each line slides up out of an overflow mask.
const MaskedLine = ({ children, delay = 0, opened, className = "", ...rest }) => (
  <span className="block overflow-hidden py-[0.08em]" {...rest}>
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={opened ? { y: "0%" } : { y: "115%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// Slow-drifting gold dust motes floating across the hero.
const GoldDust = () => {
  const motes = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        left: `${(i * 61 + 7) % 100}%`,
        top: `${12 + ((i * 37) % 74)}%`,
        size: 2 + (i % 3),
        duration: 5 + (i % 5),
        delay: (i * 0.7) % 4,
      })),
    [],
  );

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {motes.map((m, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-gold-bright"
          style={{ left: m.left, top: m.top, width: m.size, height: m.size }}
          animate={{ y: [0, -26, 0], opacity: [0, 0.7, 0] }}
          transition={{
            duration: m.duration,
            delay: m.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

export const Hero = ({ opened }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const mandalaY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const frameY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const { couple, hero } = weddingConfig;

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-emerald-dark"
    >
      {/* parallax mandala backdrop — kept faint so text stays readable */}
      <motion.div
        style={{ y: mandalaY }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <Mandala className="h-[135vmin] w-[135vmin] animate-[spin_180s_linear_infinite] text-gold/[0.09]" />
      </motion.div>

      <GoldDust />

      {/* marigold toran across the top edge */}
      <Toran className="relative z-10 block h-28 w-full sm:h-36" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex max-w-2xl flex-col items-center px-6 pb-28 pt-8 text-center sm:pt-12"
      >
        <MaskedLine
          opened={opened}
          delay={0.15}
          className="font-sans text-[11px] font-medium uppercase tracking-[0.45em] text-gold sm:text-xs"
        >
          {hero.caption}
        </MaskedLine>

        <h1 data-testid="hero-couple-names" className="mt-9">
          <MaskedLine
            opened={opened}
            delay={0.32}
            className="font-serif text-6xl font-medium tracking-wide text-cream sm:text-7xl md:text-8xl"
          >
            {couple.firstName}
          </MaskedLine>
          <MaskedLine
            opened={opened}
            delay={0.42}
            className="mt-2.5 font-sans text-[9px] uppercase tracking-[0.35em] text-cream/55"
            data-testid="hero-parents-groom"
          >
            {couple.firstParents}
          </MaskedLine>
          <MaskedLine
            opened={opened}
            delay={0.56}
            className="mb-1 mt-3 font-script text-3xl text-gold-bright sm:text-4xl"
          >
            {hero.joiner}
          </MaskedLine>
          <MaskedLine
            opened={opened}
            delay={0.7}
            className="font-serif text-6xl font-medium tracking-wide text-cream sm:text-7xl md:text-8xl"
          >
            {couple.secondName}
          </MaskedLine>
          <MaskedLine
            opened={opened}
            delay={0.82}
            className="mt-2.5 font-sans text-[9px] uppercase tracking-[0.35em] text-cream/55"
            data-testid="hero-parents-bride"
          >
            {couple.secondParents}
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={opened ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-11 flex w-60 items-center gap-3"
        >
          <span className="h-px flex-1 bg-gold/60" />
          <span className="rotate-45 border border-gold/70 p-[3px]">
            <span className="block h-1 w-1 bg-gold" />
          </span>
          <span className="h-px flex-1 bg-gold/60" />
        </motion.div>

        <MaskedLine
          opened={opened}
          delay={1.0}
          className="mt-9 font-sans text-sm font-medium uppercase tracking-[0.4em] text-gold-bright sm:text-base"
        >
          {hero.dateLine}
        </MaskedLine>
        <MaskedLine
          opened={opened}
          delay={1.1}
          className="mt-2.5 font-sans text-xs uppercase tracking-[0.5em] text-cream/70 sm:text-sm"
        >
          {hero.locationLine}
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={opened ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="relative"
        >
          {/* soft navy glow so the countdown sits clear of the mandala */}
          <div
            aria-hidden="true"
            className="absolute -inset-x-20 -inset-y-4 rounded-full bg-emerald-dark/90 blur-2xl"
          />
          <p className="relative mt-9 text-center font-sans text-[9px] uppercase tracking-[0.4em] text-gold/80">
            {hero.countdownLabel}
          </p>
          <div className="relative">
            <Countdown />
          </div>
        </motion.div>

        {/* IMAGE SLOT 1 · hero avatar — set weddingConfig.hero.image */}
        <motion.div
          style={{ y: frameY }}
          initial={{ opacity: 0, y: 60 }}
          animate={opened ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ duration: 1.1, delay: 1.35, ease: EASE }}
          className="mt-14"
        >
          <MonogramFrame
            testId="hero-avatar-slot"
            image={hero.image}
            alt={hero.imageAlt}
            label={hero.placeholderLabel}
            className="w-72 sm:w-80"
          />
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={opened ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 1.9, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="font-sans text-[9px] uppercase tracking-[0.4em] text-gold/60">
          Scroll
        </span>
        <motion.span
          className="block h-9 w-px bg-gradient-to-b from-gold/70 to-transparent"
          animate={{ scaleY: [0.3, 1, 0.3], opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};
